import { Component, Injector, Input, OnInit, inject } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Asset } from '@app/models/asset/asset';
import { WorkOrder } from '@app/models/order/work-order';
import { DyeingColor } from '@app/models/production/dyeing/dyeing-color';
import { ProductionCard } from '@app/models/production/production-card';
import { AssetProxyService } from '@app/proxies/asset/asset.proxy.service';
import { LabDipRequestProxyService } from '@app/proxies/item/lab-dip-request-proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { DyeingForecastService } from '@app/services/dyeing-forecast.service';
import { EnumService } from '@app/services/enum.service';
import { ProductionCardFormGroup } from '@app/models/production/production-card-forms';
import { ProductionCardService } from '@app/services/production-card.service';
import { BaseModalComponent } from '@card-software/library';
import { MessageService } from 'primeng/api';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { ApiResponse } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-production-cards-selection',
  templateUrl: './production-cards-selection.component.html',
  styleUrl: './production-cards-selection.component.scss',
})
export class ProductionCardsSelectionComponent
  extends BaseModalComponent<FormArray<FormGroup<ProductionCardFormGroup>> | null>
  implements OnInit
{
  @Input() currentProductionCards: FormArray<FormGroup<ProductionCardFormGroup>> | null = null;
  @Input() previouslySelectedProductionCards: ProductionCard[] = [];

  workOrder: WorkOrder | null = null;
  productionCardsSelected: FormArray<FormGroup<ProductionCardFormGroup>> = new FormArray<FormGroup<ProductionCardFormGroup>>([]);
  previouslySelectedProductionCardsFormArray: FormArray<FormGroup<ProductionCardFormGroup>> = new FormArray<
    FormGroup<ProductionCardFormGroup>
  >([]);
  allColors: DyeingColor[] = [];
  allAssets: Asset[] = [];
  listProductionCardsSubscription!: Subscription;

  private readonly productionCardsProxyService = inject(ProductionCardsProxyService);
  private productionCardService = inject(ProductionCardService);
  private readonly messageService = inject(MessageService);
  private readonly dyeingForecastService = inject(DyeingForecastService);
  private labDipRequestProxyService = inject(LabDipRequestProxyService);
  private assetsProxyService = inject(AssetProxyService);
  private enumService = inject(EnumService);

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    const assetsObservable = this.assetsProxyService.getAllMachineOptionCorrectWay();

    let colorsObservable: Observable<ApiResponse<DyeingColor[]>>;
    if (this.workOrder!.orderType === 0 /* Sample */) {
      const colorIds = [
        ...new Set(
          this.workOrder!.fabrics.flatMap((f) => f.garmetDetails || [])
            .map((gd) => gd.colorReference)
            .filter((cr): cr is string => cr != null),
        ),
      ];
      colorsObservable = this.labDipRequestProxyService.getColorsByIds(colorIds);
    } else {
      colorsObservable = this.labDipRequestProxyService.getCustomerColors(this.workOrder!.customerReference!);
    }

    this.listProductionCardsSubscription = forkJoin([assetsObservable, colorsObservable]).subscribe({
      next: ([assets, colors]) => {
        this.allAssets = assets.data!.items;
        this.allColors = colors.data!;
        this.productionCardService.initialize(this.workOrder!, colors.data!, this.allAssets, true);
        this.dyeingForecastService.initialize(this.workOrder!, colors.data!, assets.data!.items);
        this.previouslySelectedProductionCards.forEach((pc) => {
          const formGroup = this.productionCardService.createProductionCardFormGroup(pc);
          this.previouslySelectedProductionCardsFormArray.push(formGroup);
        });
      },
    });
  }

  OnDestroy(): void {
    if (this.listProductionCardsSubscription) {
      this.listProductionCardsSubscription.unsubscribe();
    }
  }

  onClose() {
    this.close({ action: 'close', data: this.productionCardsSelected });
  }

  getDyeingColor(id: string): DyeingColor | null {
    return this.allColors.find((color) => color.id === id) ?? null;
  }

  getDyeingAsset(id: string): Asset | null {
    return this.allAssets.find((asset) => asset.assetId === id) ?? null;
  }

  get productionCards(): FormArray<FormGroup> {
    const enums = this.enumService.getEnum('ProductionCardStatus', 'productionEnums').values;

    console.log(enums);

    const allCards = this.productionCardService.productionCardsFormArray;
    const filteredCards = allCards.controls.filter((formGroup) => {
      const status = formGroup.get('status')?.value;
      return status !== null && status! != 8;
    });
    return new FormArray(filteredCards);
  }

  get isLoading() {
    return this.productionCardService.isLoading();
  }

  pushProductionCardSelected($event: FormGroup<ProductionCardFormGroup>) {
    if (this.productionCardsSelected.value.find((pc) => pc.id === $event.value.id)) {
      return;
    }
    this.productionCardsSelected.push($event);
  }

  removeProductionCardSelected($event: FormGroup<ProductionCardFormGroup>) {
    this.productionCardsSelected.removeAt(this.productionCardsSelected.value.findIndex((pc) => pc.id === $event.value.id));
  }

  verifyIfProductionCardIsSelected(card: FormGroup<ProductionCardFormGroup>): boolean {
    if (!this.currentProductionCards) return false;
    return this.currentProductionCards.controls.some((ctrl) => ctrl.get('id')?.value === card.get('id')?.value);
  }

  get filteredProductionCards(): FormArray<FormGroup> {
    // if (!this.currentProductionCards) {
    //   return new FormArray([]);
    // }

    const allCards = this.currentProductionCards!;
    const filteredCards = allCards.controls.filter((formGroup) => {
      const status = formGroup.get('status')?.value;
      return status !== null && status !== 8; // Exclude deleted status
    });

    return new FormArray(filteredCards);
  }
}
